import React, { useEffect, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import timeGridPlugin from "@fullcalendar/timegrid";
import {
  Box,
  Select,
  MenuItem,
  FormControl,
  Button,
  Alert,
  TextField,
  Card,
  Grid,
  Chip,
  Typography,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  FormLabel,
} from "@mui/material";
import {
  FilterList,
  Clear,
  Person,
  Event,
  Edit,
  Delete,
  Add,
  CheckCircle,
  Cancel,
  Schedule,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import "dayjs/locale/en-gb";
import {
  applyHolidayFilters,
  createHolidayAbsence,
  deleteHolidayAbsence,
  holidayEventsList,
  updateHolidayAbsence,
  updateHolidayFilters,
} from "../redux/Thunk/holidayCalendarModule/HolidayplannerThunk";
import { getGeneralMasterDropDownList } from "../redux/Thunk/DashboardModule/DashboardThunk";
import DropdownComponent from "./DropdownComponent";
import DatePickerComponent from "./DatePickerComponent";
import PopupModal from "./Ui_elements/PopupModal";
import Btn from "./Ui_elements/Btn";

const HolidayPlanner = () => {
  const dispatch = useDispatch();
  const { user } = useSelector(state => state?.root?.authUser);
  const isFreelancer = user?.iFkUserTypeId === 2; // ✅ check freelancer
  const loginUserId = user?.iUserId;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const { holidayEvents, filters, error } = useSelector(state => state.root.HolidayPlannerModule);
  const { dropdowns } = useSelector(state => state.root.DashboardModule);
  const { languages = [], resource = [] } = dropdowns;

  const [open, setOpen] = useState(false);
  const [selectedLinguist, setSelectedLinguist] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [notes, setNotes] = useState("");
  const [rangeStart, setRangeStart] = useState(null);
  const [rangeEnd, setRangeEnd] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  // language pair state
  const [languagePairs, setLanguagePairs] = useState([]);
  const [tempSource, setTempSource] = useState("");
  const [tempTarget, setTempTarget] = useState("");

  // status mapping
  const statusColors = {
    FULL: { color: "#4caf50", label: "Available", icon: <CheckCircle /> },
    HALF_AM: { color: "#ff9800", label: "Half Day (AM)", icon: <Schedule /> },
    HALF_PM: { color: "#ff9800", label: "Half Day (PM)", icon: <Schedule /> },
    LIMITED: { color: "#2196f3", label: "Limited", icon: <Schedule /> },
    ABSENT: { color: "#f44336", label: "Not Available", icon: <Cancel /> },
  };

  // open add form
  const handleDateClick = info => {
    setEditingEvent(null);
    setRangeStart(dayjs(info.dateStr));
    setRangeEnd(dayjs(info.dateStr));
    setSelectedLinguist("");
    setSelectedStatus("");
    setNotes("");
    setLanguagePairs([]);
    setOpen(true);
  };

  // open edit form
  const handleEventClick = info => {
    const ev = info.event;
    setEditingEvent(ev);
    const endDate = ev.endStr ? dayjs(ev.endStr).subtract(1, "day") : dayjs(ev.startStr);

    setRangeStart(dayjs(ev.startStr));
    setRangeEnd(endDate);
    setSelectedLinguist(ev.extendedProps.linguistId || "");
    setSelectedStatus(ev.extendedProps.status || "");
    setNotes(ev.extendedProps.vNotes || "");
    setLanguagePairs(
      (ev.extendedProps.languages || []).map(l => ({
        source: l.source_lang,
        target: l.target_lang,
      }))
    );
    setOpen(true);
  };

  // save or update
  const handleSave = () => {
    if (!selectedStatus || !rangeStart || !rangeEnd) return;

    const payload = {
      user_id: isFreelancer ? loginUserId : selectedLinguist, // ✅ freelancer always himself
      date_from: dayjs(rangeStart).format("YYYY-MM-DD"),
      date_to: dayjs(rangeEnd).format("YYYY-MM-DD"),
      eStatus: selectedStatus,
      vNotes: notes,
      languages: languagePairs.map(p => ({
        source_lang: p.source,
        target_lang: p.target,
      })),
    };

    if (editingEvent) {
      dispatch(updateHolidayAbsence({ id: editingEvent.id, payload }));
    } else {
      dispatch(createHolidayAbsence(payload));
      dispatch(holidayEventsList());
    }
    setOpen(false);
  };

  // delete
  const handleDelete = () => {
    if (editingEvent) {
      dispatch(deleteHolidayAbsence(editingEvent.id));
      setOpen(false);
    }
  };

  // clear filters
  const handleClearFilters = () => {
    dispatch(
      updateHolidayFilters({
        sourceLanguage: "",
        targetLanguage: "",
        resource: "",
        status: "",
        showAbsentOnly: false,
        showPresentOnly: false,
        startDate: "",
        endDate: "",
      })
    );
    if (isFreelancer) {
      dispatch(updateHolidayFilters({ resource: loginUserId }));
      //dispatch(applyHolidayFilters());
    } else {
      dispatch(holidayEventsList());
    }
  };

  useEffect(() => {
    if (isFreelancer) {
      // ✅ freelancer only his own data
      dispatch(updateHolidayFilters({ resource: loginUserId }));
      dispatch(applyHolidayFilters());
      dispatch(getGeneralMasterDropDownList({ languages: true })); // no resource list
    } else {
      dispatch(holidayEventsList());
      dispatch(getGeneralMasterDropDownList({ languages: true, resource: true }));
    }
  }, [dispatch, isFreelancer, loginUserId]);

  const handleDatesSet = info => {
    dispatch(updateHolidayFilters({ startDate: info.startStr, endDate: info.endStr }));
  };

  // dropdown options
  const resourceOptions = resource.map(r => ({
    value: r.iUserId,
    label: r.vUserName,
  }));
  const languagesOptions = languages.map(l => ({
    value: l.lang_id,
    label: l.title,
  }));

  // helpers for language pairs
  const addLanguagePair = () => {
    if (!tempSource || !tempTarget) return;
    setLanguagePairs(prev => [...prev, { source: tempSource, target: tempTarget }]);
    setTempSource("");
    setTempTarget("");
  };
  const removePair = idx => setLanguagePairs(prev => prev.filter((_, i) => i !== idx));

  const handleAddAvailability = () => {
    setEditingEvent(null);
    setRangeStart(dayjs());
    setRangeEnd(dayjs());
    setSelectedLinguist("");
    setSelectedStatus("");
    setNotes("");
    setOpen(true);
  };

  // modal content
  const renderModalContent = () => (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={2}>
        {/* Linguist - hide if freelancer */}
        {!isFreelancer && (
          <Grid item xs={6}>
            <FormControl fullWidth>
              <FormLabel>Linguist</FormLabel>
              <Select
                value={selectedLinguist}
                onChange={e => setSelectedLinguist(e.target.value)}
                startAdornment={<Person sx={{ mr: 1, color: "text.secondary" }} />}
              >
                <MenuItem value="">Select Linguist</MenuItem>
                {resource.map(ling => (
                  <MenuItem key={ling.iUserId} value={ling.iUserId}>
                    {ling.vUserName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        {/* Status */}
        <Grid item xs={isFreelancer ? 12 : 6}>
          <FormControl fullWidth>
            <FormLabel>Status</FormLabel>
            <Select
              value={selectedStatus}
              onChange={e => setSelectedStatus(e.target.value)}
              renderValue={value => {
                if (!value) return "Select Status";
                const cfg = statusColors[value] || statusColors.ABSENT;
                return (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ color: cfg.color }}>{cfg.icon}</Box>
                    {cfg.label}
                  </Box>
                );
              }}
            >
              {Object.entries(statusColors).map(([key, cfg]) => (
                <MenuItem key={key} value={key}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ color: cfg.color }}>{cfg.icon}</Box>
                    {cfg.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Language Pairs */}
        <Grid item xs={12}>
          <Typography variant="subtitle1">Language Pairs</Typography>
          <Grid container spacing={1}>
            <Grid item xs={5}>
              <FormControl fullWidth size="small">
                <FormLabel>Source</FormLabel>
                <Select value={tempSource} onChange={e => setTempSource(e.target.value)}>
                  {languagesOptions.map(l => (
                    <MenuItem key={l.value} value={l.value}>
                      {l.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={5}>
              <FormControl fullWidth size="small">
                <FormLabel>Target</FormLabel>
                <Select value={tempTarget} onChange={e => setTempTarget(e.target.value)}>
                  {languagesOptions.map(l => (
                    <MenuItem key={l.value} value={l.value}>
                      {l.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={2} mt={3}>
              <Button fullWidth variant="contained" onClick={addLanguagePair}>
                Add
              </Button>
            </Grid>
          </Grid>

          <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
            {languagePairs.map((p, idx) => {
              const s = languages.find(l => l.lang_id === p.source)?.title || p.source;
              const t = languages.find(l => l.lang_id === p.target)?.title || p.target;
              return (
                <Chip
                  key={idx}
                  label={`${s} > ${t}`}
                  onDelete={() => removePair(idx)}
                  color="primary"
                  variant="outlined"
                />
              );
            })}
          </Box>
        </Grid>

        {/* Dates */}
        <Grid item xs={12} sm={6}>
          <DatePickerComponent
            label="From Date"
            value={rangeStart ? dayjs(rangeStart).format("YYYY-MM-DD") : ""}
            onChange={e => setRangeStart(dayjs(e.target.value))}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <DatePickerComponent
            label="To Date"
            value={rangeEnd ? dayjs(rangeEnd).format("YYYY-MM-DD") : ""}
            onChange={e => setRangeEnd(dayjs(e.target.value))}
          />
        </Grid>

        {/* Notes */}
        <Grid item xs={12}>
          <FormLabel>Notes</FormLabel>
          <TextField
            value={notes}
            onChange={e => setNotes(e.target.value)}
            fullWidth
            multiline
            rows={3}
            placeholder="Add notes..."
          />
        </Grid>
      </Grid>

      {/* Actions */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
        {editingEvent && (
          <Button color="error" onClick={handleDelete} startIcon={<Delete />}>
            Delete
          </Button>
        )}
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button onClick={() => setOpen(false)} variant="outlined">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!selectedStatus || !rangeStart || !rangeEnd}
            startIcon={editingEvent ? <Edit /> : <Add />}
          >
            {editingEvent ? "Update" : "Save"}
          </Button>
        </Box>
      </Box>
    </Box>
  );

  // calendar event content
  const renderEventContent = eventInfo => {
    const status = eventInfo.event.extendedProps.status;
    const cfg = statusColors[status] || statusColors.ABSENT;
    return (
      <Box
        sx={{
          background: cfg.color,
          color: "white",
          px: 1,
          py: 0.5,
          borderRadius: 1,
          fontSize: "0.75rem",
          fontWeight: 500,
          display: "flex",
          alignItems: "center",
          gap: 0.5,
        }}
      >
        {cfg.icon}
        <span>{eventInfo.event.title}</span>
      </Box>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
      <Box sx={{ p: { xs: 1, md: 3 } }}>
        {/* Header */}
        <Card sx={{ mb: 3, p: 3, border: 1, borderColor: "divider" }}>
          <Grid container spacing={3} alignItems="center">
            {/* Title and Status Chips Section */}
            <Grid item xs={12} md={8}>
              <Box>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  Holiday Planner
                </Typography>
                <Grid container spacing={1}>
                  {Object.entries(statusColors).map(([k, cfg]) => (
                    <Grid item key={k}>
                      <Chip
                        icon={cfg.icon}
                        label={cfg.label}
                        sx={{
                          background: cfg.color,
                          color: "white",
                          "& .MuiChip-icon": { color: "white" },
                        }}
                        size="small"
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Grid>

            {/* Button Section */}
            <Grid item xs={12} md={4} sx={{ display: "flex", justifyContent: { md: "flex-end" } }}>
              <Btn
                variant="contained"
                startIcon={<Add />}
                onClick={handleAddAvailability}
                sx={{ width: { xs: "100%", md: "auto" } }}
              >
                Add Availability
              </Btn>
            </Grid>
          </Grid>
        </Card>

        {/* Errors */}
        {error.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error.join(", ")}
          </Alert>
        )}

        {/* Filters */}
        { (!isFreelancer) && (
          <Card sx={{ mb: 3, p: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: showFilters ? 2 : 0 }}>
              <Typography variant="h6">Filters</Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Tooltip title="Clear Filters">
                  <IconButton onClick={handleClearFilters} size="small">
                    <Clear />
                  </IconButton>
                </Tooltip>
                <Button
                  startIcon={<FilterList />}
                  onClick={() => setShowFilters(!showFilters)}
                  variant="outlined"
                  size="small"
                >
                  {showFilters ? "Hide Filters" : "Show Filters"}
                </Button>
              </Box>
            </Box>

            {showFilters && (
              <Grid container spacing={2} alignItems="flex-end">
                {/* Linguist - hide if freelancer */}
                {!isFreelancer && (
                  <Grid item xs={12} md={3}>
                    <DropdownComponent
                      label="Linguist"
                      options={resourceOptions}
                      name="resource"
                      value={filters.resource || ""}
                      onChange={e => dispatch(updateHolidayFilters({ resource: e.target.value }))}
                    />
                  </Grid>
                )}

                {/* Status */}
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <FormLabel>Status</FormLabel>
                    <Select
                      value={filters.status || ""}
                      onChange={e => dispatch(updateHolidayFilters({ status: e.target.value }))}
                    >
                      <MenuItem value="">All Status</MenuItem>
                      {Object.entries(statusColors).map(([key, cfg]) => (
                        <MenuItem key={key} value={key}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box sx={{ color: cfg.color }}>{cfg.icon}</Box>
                            {cfg.label}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Languages */}
                <Grid item xs={12} md={4}>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <DropdownComponent
                      label="Source Language"
                      options={languagesOptions}
                      name="source_language"
                      value={filters.sourceLanguage || ""}
                      onChange={e =>
                        dispatch(updateHolidayFilters({ sourceLanguage: e.target.value }))
                      }
                    />
                    <DropdownComponent
                      label="Target Language"
                      options={languagesOptions}
                      name="target_language"
                      value={filters.targetLanguage || ""}
                      onChange={e =>
                        dispatch(updateHolidayFilters({ targetLanguage: e.target.value }))
                      }
                    />
                  </Box>
                </Grid>

                {/* Date range */}
                <Grid item xs={12} md={5}>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <DatePickerComponent
                      label="Start Date"
                      value={filters.startDate || ""}
                      onChange={e => dispatch(updateHolidayFilters({ startDate: e.target.value }))}
                    />
                    <DatePickerComponent
                      label="End Date"
                      value={filters.endDate || ""}
                      onChange={e => dispatch(updateHolidayFilters({ endDate: e.target.value }))}
                    />
                  </Box>
                </Grid>

                {/* Apply */}
                <Grid item xs={12} md={2}>
                  <Button
                    variant="contained"
                    onClick={() => dispatch(applyHolidayFilters())}
                    fullWidth
                    startIcon={<FilterList />}
                  >
                    Apply
                  </Button>
                </Grid>
              </Grid>
            )}
          </Card>
        )}

        {/* Calendar */}
        <Card sx={{ p: 2 }}>
          <FullCalendar
            plugins={[dayGridPlugin, interactionPlugin, timeGridPlugin]}
            initialView="dayGridMonth"
            firstDay={1}
            headerToolbar={{
              left: "prev,next today",
              center: "title",
              right: "dayGridMonth,timeGridWeek,timeGridDay",
            }}
            events={holidayEvents}
            eventContent={renderEventContent}
            dateClick={handleDateClick}
            eventClick={handleEventClick}
            selectable
            datesSet={handleDatesSet}
            height="auto"
          />
        </Card>

        {/* Modal */}
        <PopupModal
          open={open}
          handleClose={() => setOpen(false)}
          title={editingEvent ? "Edit Availability" : "Add Availability"}
          maxWidth="md"
          fullWidth
        >
          {renderModalContent()}
        </PopupModal>
      </Box>
    </LocalizationProvider>
  );
};

export default HolidayPlanner;
